import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight, Code2, Search, Plug, Zap, Server, Globe,
  Megaphone, ShoppingBag, Bot, Cpu, Wand2
} from "lucide-react";

const SERVICES = [
  { id: "website-design", icon: Code2, title: "Website Design & Development", desc: "Professional, responsive websites that convert visitors into customers. Built mobile-first with conversion optimization at the core.", price: "From ₹14,999", features: ["Responsive design", "SEO ready", "Custom CMS", "1-week delivery"] },
  { id: "seo", icon: Search, title: "SEO Optimization", desc: "Rank higher on Google and drive organic traffic to your website. On-page + off-page + technical SEO.", price: "₹9,999/mo", features: ["Keyword research", "Backlinks", "Monthly reports", "Tech audit"] },
  { id: "api-integration", icon: Plug, title: "API Integration", desc: "Seamlessly connect your systems and automate data flow with REST, GraphQL, OAuth and webhooks.", price: "From ₹19,999", features: ["REST/GraphQL", "OAuth & JWT", "Webhooks", "Documentation"] },
  { id: "automation", icon: Zap, title: "Business Automation", desc: "Automate repetitive tasks and focus on growing your business. Custom workflows similar to Zapier/Make.", price: "From ₹29,999", features: ["5+ workflows", "Triggers & schedules", "API connectors", "Audit logs"] },
  { id: "hosting", icon: Server, title: "Hosting Services", desc: "Cloud hosting with cPanel/WHM, Cloudflare CDN, free SSL and daily backups.", price: "From ₹1,999/yr", features: ["99.99% uptime", "Free SSL", "Daily backups", "24/7 support"] },
  { id: "domain", icon: Globe, title: "Domain Registration", desc: "150+ TLDs supported with WHOIS privacy, transfer assistance, and DNS management.", price: "From ₹599", features: ["150+ TLDs", "WHOIS privacy", "Easy transfer", "Cloudflare DNS"] },
  { id: "marketing", icon: Megaphone, title: "AI Marketing", desc: "AI-generated ads, content, and social posts that perform across channels.", price: "From ₹4,999", features: ["Ad creatives", "Social posts", "Email copy", "A/B testing"] },
  { id: "ecommerce", icon: ShoppingBag, title: "Ecommerce Development", desc: "Custom storefronts with payments, inventory and analytics — Shopify-grade quality.", price: "From ₹49,999", features: ["Razorpay/Stripe", "Inventory sync", "Analytics", "Order automation"] },
  { id: "ai-website", icon: Bot, title: "AI Website Builder", desc: "Generate a complete website blueprint — pages, copy, SEO — in seconds with AI.", price: "Free trial", features: ["AI-generated pages", "SEO metadata", "Color palette", "Blog topics"] },
  { id: "ai-app", icon: Cpu, title: "AI App Builder", desc: "From idea to schema, API and UI. AI-generated SaaS scaffolding ready to ship.", price: "Free trial", features: ["Entity models", "API routes", "UI screens", "Milestones"] },
];

export default function Services() {
  return (
    <div>
      <section className="hero-blue py-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            Our <span style={{color:'var(--accent-yellow)'}}>Services</span>
          </h1>
          <p className="text-blue-100 mt-4 text-lg">Comprehensive digital solutions to accelerate your business growth.</p>
        </div>
      </section>

      <section className="py-16 px-5">
        <div className="max-w-6xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => (
            <motion.div
              id={s.id}
              key={s.id}
              initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
              transition={{duration:.4, delay:i*0.04}}
              className="card p-7 flex flex-col scroll-mt-24"
            >
              <div className="icon-tile mb-5"><s.icon size={22}/></div>
              <h3 className="text-xl font-bold text-white mb-3" style={{fontFamily:'Outfit'}}>{s.title}</h3>
              <p className="text-slate-400 leading-relaxed text-sm flex-1">{s.desc}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                {s.features.map(f => <li key={f} className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-blue-400"/> {f}</li>)}
              </ul>
              <div className="mt-5 flex items-center justify-between border-t border-slate-700/60 pt-4">
                <span className="text-blue-400 text-sm font-semibold">{s.price}</span>
                <Link to="/contact" className="text-blue-400 text-sm inline-flex items-center gap-1 hover:gap-2 transition-all font-semibold">
                  Learn More <ArrowRight size={14}/>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-12 px-5" style={{background: 'var(--bg-section)'}}>
        <div className="max-w-4xl mx-auto text-center">
          <Wand2 className="mx-auto text-blue-400 mb-3" size={28}/>
          <h3 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>Not sure where to start?</h3>
          <p className="text-slate-400 mt-2">Tell us about your project and we'll recommend the right combination.</p>
          <Link to="/contact" className="btn-primary mt-6 inline-flex">Talk to an expert <ArrowRight size={16}/></Link>
        </div>
      </section>
    </div>
  );
}
